import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';

import type moment from 'moment-timezone';
import { startWith, switchMap } from 'rxjs';

import { TimeService } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { DatePickerComponent, DatePickerDirection } from '@core/time-ui';
import {
  BadgeComponent,
  ConnectedOverlayDirective,
  DropdownComponent,
  DropdownItemComponent,
  IconButtonComponent,
  IconComponent,
  IconToken,
  InputComponent,
  OverlayOriginDirective,
  Select2Component,
  Select2Item,
  Select2ListItemDirective,
  Select2TriggerComponent,
} from '@core/user-interface-legacy';

// `id` is null for a row the user just added: the history endpoint creates those from the same
// payload that rewrites the rest.
export type PositionRowForm = FormGroup<{
  id: FormControl<number | null>;
  positionGuid: FormControl<string | null>;
  startDate: FormControl<string | null>;
}>;

@Component({
  selector: 'tlc-position-history-edit-card',
  templateUrl: './position-history-edit-card.component.html',
  styleUrls: ['./position-history-edit-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    BadgeComponent,
    CdkDragHandle,
    ConnectedOverlayDirective,
    DatePickerComponent,
    DropdownComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    OverlayOriginDirective,
    Select2Component,
    Select2ListItemDirective,
    Select2TriggerComponent,
    TranslatePipe,
  ],
})
export class PositionHistoryEditCardComponent {
  private readonly timeService = inject(TimeService);

  protected readonly startDateControl = computed(() => this.form().controls.startDate);
  protected readonly startDateValue = toSignal(
    toObservable(this.startDateControl).pipe(
      switchMap(control => control.valueChanges.pipe(startWith(control.value))),
    ),
    { initialValue: null },
  );
  protected readonly startDateLabel = computed(() => {
    const value = this.startDateValue();
    return value ? this.timeService.format({ input: value, pattern: 'D MMM YYYY' }) : null;
  });

  protected readonly dragHandleToken = computed<IconToken | undefined>(() =>
    this.isDraggable() ? undefined : 'colorIconDisabled',
  );

  public readonly form = input.required<PositionRowForm>();
  public readonly positionItems = input.required<Array<Select2Item<string>>>();
  public readonly dateDirection = input<DatePickerDirection>('any');
  public readonly dateBlackout = input<((date: moment.Moment) => boolean) | null>(null);
  public readonly showDelete = input(false);
  public readonly isDraggable = input(false);

  public readonly deleteClicked = output<void>();
}
